import "core-js/modules/es6.regexp.match";
import "core-js/modules/es6.object.assign";
import "core-js/modules/web.dom.iterable";
import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.object.keys";
import React from 'react';
import invariant from 'tiny-invariant';
import { Box } from 'react-polymorphic-box';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var defaultElement = 'div';
/**
 * Basic building block for all components. Defaults to a `<div>`. The rendered
 * element can be overriden using the `as` prop.
 *
 * @example
 * Basic usage:
 * <View>Content</View>
 *
 * @example
 * With a `<main>` element:
 * <View as="main">Content</View>
 *
 * @example
 * With a custom component:
 * <View as={Comp}>Content</View>
 */

var View = /*#__PURE__*/React.forwardRef(function (_ref, innerRef) {
  var props = _objectWithoutPropertiesLoose(_ref, ["ref"]);

  return React.createElement(Box, Object.assign({
    ref: innerRef,
    as: defaultElement
  }, props));
}); // @ts-ignore: Component type does not currently include displayName

View.displayName = 'View';
var defaultElement$1 = 'div';
/**
 * `<View>` sized with a specified aspect ratio.
 *
 * @example
 * With a 16:9 ratio:
 * <AspectRatio x={16} y={9}>Content</AspectRatio>
 */

var AspectRatio = /*#__PURE__*/React.forwardRef(function (_ref, innerRef) {
  var x = _ref.x,
      y = _ref.y,
      children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, ["ref", "x", "y", "children"]);

  !(x > 0) ? process.env.NODE_ENV !== "production" ? invariant(false, 'Prop x must be a number greater than 0.') : invariant(false) : void 0;
  !(y >= 0) ? process.env.NODE_ENV !== "production" ? invariant(false, 'Prop y must be a number greater than or equal to 0.') : invariant(false) : void 0;
  return React.createElement(View, Object.assign({
    ref: innerRef,
    as: defaultElement$1,
    "data-system-aspect-ratio": ""
  }, props), React.createElement("div", {
    "data-system-aspect-ratio-spacer": "",
    "aria-hidden": true,
    style: {
      paddingTop: y / x * 100 + "%"
    }
  }), React.createElement("div", {
    "data-system-aspect-ratio-inner": ""
  }, children));
}); // @ts-ignore: Component type does not currently include displayName

AspectRatio.displayName = 'AspectRatio';
var defaultElement$2 = 'div';
/**
 * `<View>` with a horizontally centered nested `<div>` with an optional
 * maximum width. Gutter width should be added directly to `<BoundedView>` as
 * padding.
 *
 * @example
 * With no inner maximum width:
 * <BoundedView>Content</BoundedView>
 *
 * @example
 * With inner maximum width of `48rem`:
 * <BoundedView maxWidth="48rem">Content</BoundedView>
 */

var BoundedView = /*#__PURE__*/React.forwardRef(function (_ref, innerRef) {
  var maxWidth = _ref.maxWidth,
      innerProps = _ref.innerProps,
      children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, ["ref", "maxWidth", "innerProps", "children"]);

  return React.createElement(View, Object.assign({
    ref: innerRef,
    as: defaultElement$2
  }, props), React.createElement("div", Object.assign({
    "data-system-bounded-view-inner": "",
    style: {
      maxWidth: maxWidth
    }
  }, innerProps), children));
}); // @ts-ignore: Component type does not currently include displayName

BoundedView.displayName = 'BoundedView';
var defaultElement$3 = 'div';
/**
 * `<View>` with flexbox display type.
 *
 * @example
 * Basic usage:
 * <Flex>Content</Flex>
 *
 * @example
 * With flex CSS properties:
 * <Flex css={{ flexDirection: 'column' }}>Content</Flex>
 */

var Flex = /*#__PURE__*/React.forwardRef(function (_ref, innerRef) {
  var props = _objectWithoutPropertiesLoose(_ref, ["ref"]);

  return React.createElement(View, Object.assign({
    ref: innerRef,
    as: defaultElement$3,
    "data-system-flex": ""
  }, props));
}); // @ts-ignore: Component type does not currently include displayName

Flex.displayName = 'Flex';
var defaultElement$4 = 'div';
/**
 * `<View>` used to wrap a `<GatsbyImage>` component when the `<GatsbyImage>`
 * component is styled to be 100% of its parent's height. This component
 * applies style overrides to the `GatsbyImage`-specific class names.
 *
 * @example
 * Basic usage:
 * <GatsbyImageContainer>
 *   <GatsbyImage fluid={fluid} css={{ height:'100%' }} />
 * </GatsbyImageContainer>
 */

var GatsbyImageContainer = /*#__PURE__*/React.forwardRef(function (_ref, innerRef) {
  var props = _objectWithoutPropertiesLoose(_ref, ["ref"]);

  return React.createElement(View, Object.assign({
    ref: innerRef,
    as: defaultElement$4,
    "data-system-gatsby-image-container": ""
  }, props));
}); // @ts-ignore: Component type does not currently include displayName

GatsbyImageContainer.displayName = 'GatsbyImageContainer';
var defaultElement$5 = 'div';
/**
 * `<View>` with gridbox display type.
 *
 * @example
 * Basic usage:
 * <Grid>Content</Grid>
 *
 * @example
 * With grid CSS properties:
 * <Grid css={{ gridDirection: 'column' }}>Content</Grid>
 */

var Grid = /*#__PURE__*/React.forwardRef(function (_ref, innerRef) {
  var props = _objectWithoutPropertiesLoose(_ref, ["ref"]);

  return React.createElement(View, Object.assign({
    ref: innerRef,
    as: defaultElement$5,
    "data-system-grid": ""
  }, props));
}); // @ts-ignore: Component type does not currently include displayName

Grid.displayName = 'Grid';
/**
 * Finds the first non-nullish value in an array starting from `idx` and
 * searching left.
 *
 * @param arr Array of values.
 * @param idx Starting search index.
 *
 * @returns First non-nullish value in the array with an index equal to or less
 * than `idx`. Returns `undefined` if no value can be found.
 */

/**
 * Determines if a URL is external. This is a naive check that does not take
 * into account the relative domain.
 *
 * @param url The URL to test.
 *
 * @returns `true` if the URL is external, `false` otherwise.
 *
 * @example
 * isExternal('https://example.com') // true
 * isExternal('/child-page') // false
 */

var isExternal = function isExternal(url) {
  return /^https?:\/\//.test(url);
};
/**
 * Determines if a URL is an anchor link.
 *
 * @param url The URL to test.
 *
 * @returns `true` if the URL is an anchor link, `false` otherwise.
 *
 * @example
 * isAnchorOnly('https://#top') // true
 * isAnchorOnly('https://example.com') // false
 * isAnchorOnly('https://example.com#top') // false
 */


var isAnchorOnly = function isAnchorOnly(url) {
  return /^https?:\/\/#/.test(url);
};
/**
 * Extracts the anchor from a URL if present.
 *
 * @param url The URL from which to extract an anchor.
 *
 * @returns The URL's anchor if present, `undefined` otherwise.
 *
 * @example
 * extractAnchor('https://#top') // #top
 * extractAnchor('https://example.com#top') // #top
 * extractAnchor('https://example.com') // undefined
 */


var extractAnchor = function extractAnchor(url) {
  var _url$match;

  return (_url$match = url.match(/#\S*$/)) === null || _url$match === void 0 ? void 0 : _url$match[0];
};

var defaultElement$6 = 'a';
/**
 * `<View>` that dynamically changes from an `<a>` to a router-specific
 * component depending on if `href` is external or internal.
 *
 * If `href` is external, `target` is automatically set to `_target` and `rel`
 * is automatically set to `nofollow noopener noreferrer`. Both can be
 * overridden by passing their respective props.
 *
 * This component should be used a base for a project-specific Link component
 * that adds a default `routerLinkComponent`.
 *
 * @example
 * With an internal `href`:
 * <Link href="/example">Example</Link>
 *
 * @example
 * With an external `href` rendered with Gatsby's Link:
 * <Link href="https://example.com" routerLinkComponent={GatsbyLink}>
 *   Example
 * </Link>
 */

var Link = /*#__PURE__*/React.forwardRef(function (_ref, innerRef) {
  var _extractAnchor;

  var rawHref = _ref.href,
      _ref$routerLinkCompon = _ref.routerLinkComponent,
      RouterLinkComponent = _ref$routerLinkCompon === void 0 ? 'a' : _ref$routerLinkCompon,
      props = _objectWithoutPropertiesLoose(_ref, ["ref", "href", "routerLinkComponent"]);

  var href = isAnchorOnly(rawHref) ? (_extractAnchor = extractAnchor(rawHref)) !== null && _extractAnchor !== void 0 ? _extractAnchor : '' : rawHref;
  var rel = isExternal(href) ? 'nofollow noopener noreferrer' : undefined;
  var target = isExternal(href) ? '_blank' : undefined;
  return React.createElement(View, Object.assign({
    ref: innerRef,
    as: isExternal(href) ? defaultElement$6 : RouterLinkComponent,
    href: href,
    target: target,
    rel: rel
  }, props));
}); // @ts-ignore: Component type does not currently include displayName

Link.displayName = 'Link';
export { AspectRatio, BoundedView, Flex, GatsbyImageContainer, Grid, Link, View };